import { Box, Combobox, FormField, Label } from '@zillow/constellation-10';
import type { InvoiceListFilterConfig } from '../../hooks/useInvoiceList';
import { BillingPortalDatePicker } from '../../components/BillingPortalDatePicker';
import { EVENT_IDS } from '../../model/ClickstreamEvents';

interface InvoiceFilterProps {
  setFilters: (newFilters: InvoiceListFilterConfig) => void;
  filters: InvoiceListFilterConfig;
  isPaymentDue: boolean;
}

const dateValidation = /\d{2}\/\d{2}\/\d{4}/;
const today = new Date();

export const InvoiceFilter = ({ filters, setFilters, isPaymentDue }: InvoiceFilterProps) => {
  return (
    <Box
      css={{
        padding: '20px 32px',
        borderTop: isPaymentDue ? 'none' : '1px solid #d1d1d5',
      }}
    >
      <Box
        css={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        <Box
          css={{
            width: '100%',
            lg: { width: 'calc(50% - 20px)' },
            xxl: { width: 'calc(33.333% - 14px)' },
          }}
        >
          <Box data-eventid={EVENT_IDS.SET_FILTER}>
            <Label css={{ display: 'inline-block', marginBottom: 4 }}>Start Date</Label>
            <BillingPortalDatePicker
              size="sm"
              maxDate={today}
              dateFormat="MM/dd/yyyy"
              shouldAwaitInteractionResponse
              value={filters.startDate}
              onValueChange={(date: string | Date) => {
                const newDate = date.toString();
                if (newDate && !dateValidation.test(newDate)) {
                  return;
                }
                setFilters({
                  ...filters,
                  startDate: newDate,
                });
              }}
            />
          </Box>
        </Box>
        <Box
          css={{
            width: '100%',
            lg: { width: 'calc(50% - 20px)' },
            xxl: { width: 'calc(33.333% - 14px)' },
          }}
        >
          <Box data-eventid={EVENT_IDS.SET_FILTER}>
            <Label css={{ display: 'inline-block', marginBottom: 4 }}>End Date</Label>
            <BillingPortalDatePicker
              size="sm"
              maxDate={today}
              dateFormat="MM/dd/yyyy"
              shouldAwaitInteractionResponse
              value={filters.endDate}
              onValueChange={(date: string | Date) => {
                const newDate = date.toString();
                if (newDate && !dateValidation.test(newDate)) {
                  return;
                }
                setFilters({
                  ...filters,
                  endDate: newDate,
                });
              }}
            />
          </Box>
        </Box>
        {filters.city.length > 1 && (
          <Box
            css={{
              '@media (max-width: 1250px)': { width: 'calc(50% - 20px)' },
              '@media (max-width: 850px)': { width: '100%' },
              '@media (min-width: 1251px)': { width: 'calc(33.333% - 14px)' },
            }}
          >
            <FormField
              data-eventid={EVENT_IDS.SET_FILTER}
              label={<Label>City</Label>}
              control={
                <Combobox
                  onChange={(values: Array<string>) => {
                    setFilters({
                      ...filters,
                      city: filters.city.map((oldValue) => ({
                        ...oldValue,
                        isChecked: values.includes(oldValue.label),
                      })),
                    });
                  }}
                  size="sm"
                  limitChipsShown={-1}
                  options={filters.city
                    .map((city) => ({
                      value: city.label,
                      label: city.label,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  defaultValue={[]}
                  showLabelForValue={true}
                  renderChip={() => <></>}
                  renderChipsOverflow={() => {
                    const selectedCount = filters.city.filter((city) => city.isChecked).length;

                    return <>{selectedCount > 0 && `+${selectedCount}`}</>;
                  }}
                  value={filters.city.filter((city) => city.isChecked).map((city) => city.label)}
                />
              }
            />
          </Box>
        )}
        {filters.state.length > 1 && (
          <Box
            css={{
              '@media (max-width: 1250px)': { width: 'calc(50% - 20px)' },
              '@media (max-width: 850px)': { width: '100%' },
              '@media (min-width: 1251px)': { width: 'calc(33.333% - 14px)' },
            }}
          >
            <FormField
              data-eventid={EVENT_IDS.SET_FILTER}
              label={<Label>State</Label>}
              control={
                <Combobox
                  size="sm"
                  limitChipsShown={-1}
                  onChange={(values: Array<string>) => {
                    setFilters({
                      ...filters,
                      state: filters.state.map((oldValue) => ({
                        ...oldValue,
                        isChecked: values.includes(oldValue.label),
                      })),
                    });
                  }}
                  value={filters.state.filter((state) => state.isChecked).map((state) => state.label)}
                  options={filters.state
                    .map((state) => ({
                      value: state.label,
                      label: state.label,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  defaultValue={[]}
                  showLabelForValue={true}
                  renderChip={() => <></>}
                  renderChipsOverflow={() => {
                    const selectedCount = filters.state.filter((state) => state.isChecked).length;

                    return <>{selectedCount > 0 && `+${selectedCount}`}</>;
                  }}
                />
              }
            />
          </Box>
        )}
        {filters.property.length > 1 && (
          <Box
            css={{
              '@media (max-width: 1250px)': { width: 'calc(50% - 20px)' },
              '@media (max-width: 850px)': { width: '100%' },
              '@media (min-width: 1251px)': { width: 'calc(33.333% - 14px)' },
            }}
          >
            <FormField
              data-eventid={EVENT_IDS.SET_FILTER}
              label={<Label>Property</Label>}
              control={
                <Combobox
                  size="sm"
                  limitChipsShown={-1}
                  onChange={(values: Array<string>) => {
                    setFilters({
                      ...filters,
                      property: filters.property.map((oldValue) => ({
                        ...oldValue,
                        isChecked: values.includes(oldValue.value as string),
                      })),
                    });
                  }}
                  options={filters.property
                    .map((property) => ({
                      value: property.value as string,
                      label: property.label,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  value={
                    filters.property
                      .filter((property) => property.isChecked)
                      .map((property) => property.value) as Array<string>
                  }
                  defaultValue={[]}
                  showLabelForValue={true}
                  renderChip={() => <></>}
                  renderChipsOverflow={() => {
                    const selectedCount = filters.property.filter((property) => property.isChecked).length;

                    return <>{selectedCount > 0 && `+${selectedCount}`}</>;
                  }}
                />
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
